import { Injectable, inject } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { AlertType } from '@shared-enums';
import { GraphQLErrorResponse } from '@shared-models';

import { AlertService } from './alert.service';

/**
 * Thin wrapper over AlertService for the common success/error feedback used
 * across forms. Centralizes the open() config so the alert signature lives in
 * one place instead of being copy-pasted into every component.
 */
@Injectable({ providedIn: 'root' })
export class AlertFeedbackService {
  private readonly alertService = inject(AlertService);
  private readonly translate = inject(TranslateService);

  /**
   * Shows a success alert. `message` is an i18n key; `params` feeds its
   * interpolation placeholders. `onDone` runs when the user accepts or closes
   * it, mirroring the previous per-component behavior (e.g. list refresh).
   */
  success(message: string, onDone?: () => void, params?: object): void {
    this.alertService
      .open({
        title: this.translate.instant('global.alerts.title.success'),
        message: this.translate.instant(message, params),
        type: AlertType.SUCCESS,
        acceptText: this.translate.instant('global.alerts.buttons.ok'),
        showClose: true,
      })
      .subscribe({ accept: () => onDone?.(), close: () => onDone?.() });
  }

  /**
   * Shows an error alert. `message` is an i18n key; `params` feeds its
   * interpolation placeholders.
   */
  error(message: string, params?: object): void {
    this.alertService.open({
      title: this.translate.instant('global.alerts.title.error'),
      message: this.translate.instant(message, params),
      type: AlertType.ERROR,
      acceptText: this.translate.instant('global.alerts.buttons.ok'),
      showClose: true,
    });
  }

  /** Shows an error alert from a GraphQL error, with a generic fallback. */
  errorFromGraphQL(err: GraphQLErrorResponse): void {
    const message = err?.graphQLErrors?.[0]?.message;
    this.error(message || 'global.alerts.description.somethingWentWrong');
  }
}
